import { ReactNode } from 'react';
import { cellBorderPx, cellPaddingPx, MeasureCellItem, measureColumnWidths } from './measureColumns';
import { TextUtil } from './TextUtil';

/**
 * Single source of truth for table column widths, replacing the two pipelines that used
 * to compute them independently (box tables in BoxTable.tsx, hand-written ones in
 * XmTable.tsx) and disagreed on candidate selection and header measurement — the origin
 * of most width bugs: overflow, auto-growth, header/body desync.
 *
 * World-agnostic: callers describe each column as a {@link MeasureColumn} via a per-world
 * adapter. All measurement goes through the one hidden `<table>` in
 * {@link measureColumnWidths}, so headers and cells are sized identically.
 */

/** Column expansion behaviour. Mirrors `ExpandColsType` in Table.tsx. */
export type ExpandMode = 'none' | 'fill' | 'full';

/** Trim/overflow columns are capped here unless the mode is 'full'. */
export const COL_MAX_WIDTH = 210;
/** Description-style columns get a larger cap when filling free space. */
export const DESCRIPTION_COL_MAX_WIDTH = 450;

// Header affordance widths added on top of the measured title. The measured title already
// includes the cell's padding AND border (box-sizing: border-box in measureColumnWidths),
// so only the controls are added here. All of them are px values — they do NOT scale with
// the cell font, so a 0.9em table reserves the same as a 1em one.

/** Sorter in a `th.xm-search`: 12px icon box + our 1px margin-right. */
const TH_SORTER_SEARCH = 13;
/** Sorter with antd's spacing: 12px icon box + marginInlineStart `marginXXS` (2). */
const TH_SORTER = 14;
// Both triggers overhang into the cell padding: antd's size rule sets their
// marginInlineEnd to −(cellPaddingInlineSM / 2), so these follow that token (6 → −3).
/** Search trigger in a `th.xm-search`: box 1 + 12 + 2 = 15, margin-left 0, end −3 → 12. */
const TH_FILTER_SEARCH = 12;
/** antd's own filter trigger: box 2 + 12 + 2 = 16, margins +2 (paddingXXS) / −3 → 15. */
const TH_FILTER = 15;
/**
 * Breathing room between the title and the control cluster.
 */
const TH_CONTROL_GAP = 6;

export interface HeaderControls {
  /** Header has a visible label the controls must not be glued to. */
  hasTitle: boolean;
  hasSort?: boolean;
  hasFilter?: boolean;
  /** The filter is antd's own (`col.filters`), NOT our search dropdown — see MeasureColumn. */
  antdFilter?: boolean;
  /** Override for {@link TH_CONTROL_GAP}. */
  gap?: number;
}

/**
 * Width the header controls occupy next to the title, gap included.
 */
export const headerControlsWidth = (c: HeaderControls): number => {
  const search = c.hasFilter && !c.antdFilter;
  const controls = (c.hasSort ? (search ? TH_SORTER_SEARCH : TH_SORTER) : 0)
    + (c.hasFilter ? (search ? TH_FILTER_SEARCH : TH_FILTER) : 0);
  if (!controls) return 0;
  return controls + (c.hasTitle ? c.gap ?? TH_CONTROL_GAP : 0);
};

/**
 * Extra width for the table's own edge plus one border per column. NOTE that every MEASURED
 * width now carries its cell's border itself (the measure cell reserves cellBorderPx), so
 * for engine-sized columns this double-counts; it is still right for caller-fixed ones,
 * which skip measuring. Only feeds `layoutTotal` — the FILL math and the external-header
 * growth — never the emitted totalWidth, so the cost is that a fill table distributes up to
 * colCount px less than it could.
 */
const BORDER_ALLOWANCE = (colCount: number) => colCount + 1;

export interface MeasureColumn {
  /** Stable key identifying the column; used to read the width back out. */
  key: string;

  // ---- header ----
  /** Header content measured for the column's minimum (floor) width. */
  headerContent: ReactNode;
  /** Header renders on multiple lines (e.g. `Name\n[MiB]`) — measure widest line. */
  headerNewLines?: boolean;
  /** Column shows a sort control (adds icon width to the header floor). */
  hasSort?: boolean;
  /** Column shows a filter control (adds icon width to the header floor). */
  hasFilter?: boolean;
  /**
   * The filter is antd's own (`col.filters`), not our search dropdown. Only the latter puts
   * `xm-search` on the th, and Table.less spaces the controls tighter there — so leaving
   * this false when antd's filter is used under-reserves the header by 4px.
   */
  antdFilter?: boolean;

  // ---- cell measurement ----
  styleClass?: string;
  /** Each `\n` line renders on its own row — width = widest line. */
  newLines?: boolean;
  /** Measure candidates in bold — a `bold` styleClass cell renders at weight 700. */
  bold?: boolean;
  /**
   * Plain text of the cell for a row, used to pick the widest candidates.
   * Return `''` for cells with no measurable text (e.g. a status dot).
   */
  cellText: (row: unknown, rowIndex: number) => string;
  /**
   * Richer content to measure for a winning candidate (icons/badges/links), as
   * pre-rendered `innerHTML` or a `content` node. Omit to measure the plain text.
   */
  cellContent?: (row: unknown, text: string, rowIndex: number) => { content?: ReactNode; innerHTML?: string; };

  // ---- constraints / behaviour ----
  trim?: boolean;
  /** Caller-provided explicit width (px). Honored as-is, still floored by header. */
  fixedWidth?: number;
  /** Caller-provided minimum width (px). Raises the header-derived floor. */
  minWidth?: number;
  /** Caller-provided maximum width (px). Caps content growth (floor still wins). */
  maxWidth?: number;
  /** Description-style column — capped at DESCRIPTION_COL_MAX_WIDTH while filling. */
  isDescription?: boolean;
  /** Skip width computation (e.g. vertical orientation keeps its own width). */
  skip?: boolean;
  /** Mirrors ColProps.grow; applied in step 5b of {@link layoutColumnWidths}. */
  grow?: boolean;
}

export interface WidthOptions {
  /** Available width of the table's container (px). */
  parentWidth: number;
  /** Expansion behaviour. Default 'full' (grow every column to fit its content). */
  mode?: ExpandMode;
  /** Width of an external header (label + toolbar) the table must stay wider than. */
  tableHeaderWidth?: number;
  /** Override for the generic trim/overflow cap (default {@link COL_MAX_WIDTH}). */
  colMaxWidth?: number;
  /** Override for the description cap (default {@link DESCRIPTION_COL_MAX_WIDTH}). */
  descriptionColMaxWidth?: number;
  /**
   * Font size of the table's cells relative to the app base (12px)
   */
  fontScale?: number;
  /**
   * Override for the reserved gap between a header label and its sort/filter controls
   * (default {@link TH_CONTROL_GAP}). Pass 0 for tables whose CSS packs the label and the
   * controls adjacent on purpose — GraphTable reverses them and uses `flex-start`, so a
   * reserved gap can't separate anything and shows up as dead space at the cell edge.
   */
  controlGap?: number;
  /** Measured width (px) of the table's frame outside its columns; replaces {@link BORDER_ALLOWANCE}. */
  borderAllowance?: number;
}

export interface WidthResult {
  /** Final width per column key (ceil'd). Skipped columns are absent. */
  widths: Record<string, number>;
  /**
   * Per-column minimum (ceil'd): the caller's `minWidth`, else the header floor. antd
   * puts it on the `<col>` only under `tableLayout: auto`, as the stretch floor — so it
   * must stay the caller's intent, NOT the grown `width`, or fill tables lose their
   * proportions and stretch evenly.
   */
  minWidths: Record<string, number>;
  /** Keys of the columns that were sized, in input order. */
  order: string[];
  /** Sum of all computed widths (ceil'd) — usable as `scroll.x`. */
  totalWidth: number;
}

/**
 * Measure every header in one hidden-table batch (weight 600, antd's header weight) and
 * add the sort/filter icon affordances. The ONE header-measurement path for every table,
 * so headers and cells are sized the same way.
 *
 * Its floor also becomes the auto-layout `<col min-width>`, even for fixed-width columns
 * — so a caller width below the header can't clip it.
 */
function measureHeaderWidths(columns: MeasureColumn[], fontScale = 1, controlGap?: number): number[] {
  const items: MeasureCellItem[] = columns.map((col, i) =>
    ({ key: String(i), content: col.headerContent, newLines: col.headerNewLines, fontWeight: 600 }));
  const measured = items.length ? measureColumnWidths(items, fontScale) : {};
  return columns.map((col, i) => {
    const title = measured[String(i)] ?? 0;
    return title + headerControlsWidth({
      hasTitle: title > cellPaddingPx() + cellBorderPx(),
      hasSort: col.hasSort, hasFilter: col.hasFilter, antdFilter: col.antdFilter, gap: controlGap,
    });
  });
}

/**
 * Flatten grouped/tree data so nested `children` rows are measured too — a grouped table
 * (the mirror table, #4037) carries its real leaf values there, and measuring only the top
 * level leaves the columns too narrow. The widest candidate still wins, so this can only
 * widen.
 */
function collectRowsDeep(list: readonly unknown[], out: unknown[]): void {
  for (const r of list) {
    out.push(r);
    const kids = (r as { children?: readonly unknown[]; }).children;
    if (Array.isArray(kids)) collectRowsDeep(kids, out);
  }
}

/** Collapse whitespace; a `newLines` cell keeps its line breaks. */
function normalizeCellText(text: string, newLines?: boolean): string {
  if (!newLines) return TextUtil.collapseWhitespace(text);
  return text.split('\n').map(TextUtil.collapseWhitespace).filter(Boolean).join('\n');
}

const longestLineLength = (text: string) =>
  text.split('\n').reduce((max, line) => Math.max(max, line.length), 0);

/** Per-column inputs of {@link layoutColumnWidths}: plain data, so a cached measurement holds no closures. */
export type LayoutColumn = Pick<MeasureColumn, 'key' | 'fixedWidth' | 'trim' | 'minWidth' | 'maxWidth' | 'isDescription' | 'grow'>;

/** Content measurement of a column set; independent of the container width. */
export interface ColumnMeasurement {
  /** Columns taking part in sizing (`skip` ones removed), in input order. */
  active: LayoutColumn[];
  /** Header floor per active column (px). */
  headerFloor: number[];
  /** Widest measured cell per active column (px). */
  maxValueWidth: number[];
}

/**
 * Steps 1-3: measure headers and cell content in the hidden table. This is the expensive
 * part (renders every candidate cell) and depends only on columns, rows and font, so
 * callers cache it across container resizes and re-run just {@link layoutColumnWidths}.
 */
export function measureColumnContent(
  columns: MeasureColumn[],
  rows: readonly unknown[],
  opts: Pick<WidthOptions, 'fontScale' | 'controlGap'>,
): ColumnMeasurement {
  const active = columns.filter(c => !c.skip);
  if (!active.length) return { active, headerFloor: [], maxValueWidth: [] };

  // Grouped/tree data: measure nested children too (see collectRowsDeep).
  const measureRows: unknown[] = [];
  collectRowsDeep(rows, measureRows);

  // 1. Header floors.
  const headerFloor = measureHeaderWidths(active, opts.fontScale, opts.controlGap);

  // 2. Candidates — the K longest DISTINCT values per column, not just the char-max ties:
  // character count is only a proxy for pixel width, and in a proportional (especially
  // bold) font a shorter string can be wider — bold "System Manufacturer" (19ch, 121px)
  // beats "Cert Expiring (days)" (20ch, 112px). Keep several and let the DOM decide.
  // Caller-fixed widths don't depend on content, so those columns skip measuring entirely.
  const CANDIDATE_LIMIT = 12;
  const candidates: { text: string; row: unknown; index: number; }[][] = active.map(() => []);
  for (let ci = 0; ci < active.length; ci++) {
    const col = active[ci];
    if (col.fixedWidth != null) continue;
    const distinct = new Map<string, { row: unknown; index: number; }>(); // text -> first row that produced it
    for (let ri = 0; ri < measureRows.length; ri++) {
      const row = measureRows[ri];
      const text = normalizeCellText(col.cellText(row, ri) ?? '', col.newLines);
      if (!text || distinct.has(text)) continue;
      distinct.set(text, { row, index: ri });
    }
    const rank = col.newLines ? longestLineLength : (text: string) => text.length;
    candidates[ci] = [...distinct]
      .sort((a, b) => rank(b[0]) - rank(a[0]))
      .slice(0, CANDIDATE_LIMIT)
      .map(([text, { row, index }]) => ({ text, row, index }));

    // Cells that hold NO text — icon buttons, a Switch, a status dot — produce no candidate
    // above, so step 3 never calls cellContent and the column stays floored on its header
    // alone. Under tableLayout:auto the body table then stretches it past the header table
    // and the two desync (AlertingHWTable / IntegrationsTable "Actions"). Probe a few rows so
    // a control rendered only for some records still counts.
    if (!candidates[ci].length && col.cellContent && measureRows.length) {
      const probes = [...new Set([0, measureRows.length >> 1, measureRows.length - 1])];
      candidates[ci] = probes.map(index => ({ text: '', row: measureRows[index], index }));
    }
  }

  // 3. Measure all candidates in one hidden-table batch.
  const items: MeasureCellItem[] = [];
  active.forEach((col, ci) => {
    candidates[ci].forEach((cand, i) => {
      const rich = col.cellContent?.(cand.row, cand.text, cand.index);
      items.push({
        key: `${ci}_${i}`,
        content: rich?.content ?? cand.text,
        innerHTML: rich?.innerHTML,
        styleClass: col.styleClass,
        newLines: col.newLines,
        fontWeight: col.bold ? 700 : 400,
      });
    });
  });
  const measured = measureColumnWidths(items, opts.fontScale);
  const maxValueWidth = active.map((_, ci) =>
    candidates[ci].reduce((max, _c, i) => Math.max(max, measured[`${ci}_${i}`] ?? 0), 0));
  return {
    active: active.map(({ key, fixedWidth, trim, minWidth, maxWidth, isDescription, grow }) =>
      ({ key, fixedWidth, trim, minWidth, maxWidth, isDescription, grow })),
    headerFloor,
    maxValueWidth,
  };
}

/** Steps 4-7: fit measured columns into the container. Pure arithmetic, no DOM. */
export function layoutColumnWidths(
  { active, headerFloor, maxValueWidth }: ColumnMeasurement,
  opts: WidthOptions,
): WidthResult {
  if (!active.length) return { widths: {}, minWidths: {}, order: [], totalWidth: 0 };
  const mode: ExpandMode = opts.mode ?? 'full';
  const colMax = opts.colMaxWidth ?? COL_MAX_WIDTH;
  const descMax = opts.descriptionColMaxWidth ?? DESCRIPTION_COL_MAX_WIDTH;
  const allowance = opts.borderAllowance ?? BORDER_ALLOWANCE(active.length);
  /** Columns sized by the container, not content (FILL's last taker, GROW). */
  const elastic = new Set<number>();

  // 4. Combine + per-column rules.
  const widths = new Array<number>(active.length);
  const fullWidths = new Array<number>(active.length);
  let layoutTotal = allowance;
  for (let ci = 0; ci < active.length; ci++) {
    const col = active[ci];
    const floor = Math.max(headerFloor[ci], col.minWidth ?? 0);
    const full = Math.max(floor, maxValueWidth[ci]);
    fullWidths[ci] = full;

    let width: number;
    if (col.fixedWidth != null) {
      // Caller is authoritative, but never below the header floor — a column narrower
      // than its own title + sort/filter icons clips the header. Box/group widths already
      // clear it, so this only rescues a hand-written width set too small. remapCol must
      // apply the SAME floor: scroll.x is summed from here, so any disagreement desyncs
      // header from body.
      width = Math.max(col.fixedWidth, floor);
    } else if (col.trim || full > opts.parentWidth) {
      const capped = mode === 'full' ? maxValueWidth[ci] : Math.min(maxValueWidth[ci], colMax);
      width = Math.max(floor, capped);
    } else {
      width = full;
    }

    if (col.fixedWidth == null && col.maxWidth != null && width > col.maxWidth)
      width = Math.max(col.maxWidth, floor); // header floor still wins

    widths[ci] = width;
    layoutTotal += width;
  }

  // 5. FILL — distribute leftover container width to columns that still overflow.
  if (mode === 'fill' && opts.parentWidth - layoutTotal >= 1) {
    let free = opts.parentWidth - layoutTotal;
    for (let ci = 0; ci < active.length && free > 0; ci++) {
      const col = active[ci];
      if (col.fixedWidth != null) continue; // fixed columns never fill-expand
      if (fullWidths[ci] <= widths[ci]) continue;
      const target = col.isDescription && fullWidths[ci] > descMax ? descMax : fullWidths[ci];
      const add = Math.min(free, target - widths[ci]);
      if (add <= 0) continue;
      if (add === free) elastic.add(ci);
      widths[ci] += add;
      free -= add;
      layoutTotal += add;
    }

    // 5b. GROW — `grow` columns split what is still free, proportionally to their width,
    // so the table spans the container without widening the other columns.
    if (free > 0) {
      const grow = active.flatMap((c, ci) => c.grow && c.fixedWidth == null ? [ci] : []);
      const base = grow.reduce((sum, ci) => sum + widths[ci], 0);
      for (const ci of grow) {
        widths[ci] += free * widths[ci] / base;
        elastic.add(ci);
      }
      if (grow.length) layoutTotal += free;
    }
  }

  // 6. Grow evenly if an external header (label + toolbar) is wider than the table.
  const headerGrow = !!opts.tableHeaderWidth && opts.tableHeaderWidth > layoutTotal;
  if (headerGrow) {
    const add = Math.ceil((opts.tableHeaderWidth - layoutTotal) / active.length);
    for (let ci = 0; ci < active.length; ci++) {
      widths[ci] += add;
      layoutTotal += add;
    }
  }

  // 7. Emit by key. Elastic columns split the whole px left (largest remainder): ceil would
  // overshoot the container and a content-sized ancestor (modal) would grow forever.
  const rounded = widths.map(Math.ceil);
  if (mode === 'fill' && elastic.size && !headerGrow) {
    const cols = [...elastic];
    let room = Math.floor(opts.parentWidth - allowance)
      - rounded.reduce((sum, w, ci) => elastic.has(ci) ? sum : sum + w, 0);
    for (const ci of cols) room -= (rounded[ci] = Math.floor(widths[ci]));
    cols.sort((a, b) => (widths[b] % 1) - (widths[a] % 1));
    for (let i = 0; room !== 0; i = (i + 1) % cols.length) {
      const step = Math.sign(room);
      rounded[cols[i]] += step;
      room -= step;
    }
  }

  const out: Record<string, number> = {};
  const minWidthsOut: Record<string, number> = {};
  const order: string[] = [];
  let totalWidth = 0;
  active.forEach((col, ci) => {
    const w = rounded[ci];
    out[col.key] = w;
    // The `<col min-width>` must reflect the caller's intent (or the header
    // floor), never the grown width — see WidthResult.minWidths.
    minWidthsOut[col.key] = Math.ceil(col.minWidth ?? headerFloor[ci]);
    order.push(col.key);
    totalWidth += w;
  });
  return { widths: out, minWidths: minWidthsOut, order, totalWidth };
}
